// ═══════════════════════════════════════════════════════════════
// Driver's License barcode scanner (PDF417)
// Mirrors the proven Intake/Register.tsx pattern:
//  - Acquire camera via getUserMedia, attach to <video> manually
//  - Draw video frames into a hidden <canvas> via requestAnimationFrame
//  - Try native BarcodeDetector (best PDF417 support) first
//  - Fall back to zxing's decodeFromCanvas
// We do NOT use zxing's decodeFromVideoElement / decodeFromConstraints —
// those don't survive the Mantine Modal mount lifecycle reliably.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import {
  Modal, Button, Stack, Text, Group, Loader, Paper, ThemeIcon,
} from '@mantine/core';
import { IconBarcode, IconX } from '@tabler/icons-react';
import { parseDriversLicense, type DLData } from '../../lib/dlParser';

interface Props {
  opened: boolean;
  onClose: () => void;
  onScan: (data: DLData) => void;
}

export default function DLScanner({ opened, onClose, onScan }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const activeRef = useRef(false);
  const rafRef = useRef<number | ReturnType<typeof setTimeout> | null>(null);

  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('Point at the PDF417 barcode on the back of the ID');

  const stop = () => {
    activeRef.current = false;
    if (rafRef.current != null) {
      try { cancelAnimationFrame(rafRef.current as number); } catch { /* ignore */ }
      try { clearTimeout(rafRef.current as ReturnType<typeof setTimeout>); } catch { /* ignore */ }
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => { try { t.stop(); } catch { /* ignore */ } });
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setScanning(false);
  };

  const handleResult = (text: string) => {
    const parsed = parseDriversLicense(text);
    if (parsed) {
      onScan(parsed);
      stop();
      onClose();
    } else {
      // Got a barcode but it isn't AAMVA — keep scanning
    }
  };

  useEffect(() => {
    if (!opened) {
      stop();
      return;
    }

    activeRef.current = true;
    setError('');
    setScanning(false);

    // Rotate hints
    const tips = [
      'Point at the PDF417 barcode on the back of the ID',
      'Hold the camera 4–8 inches from the barcode',
      'The barcode is the wide striped rectangle on the back',
      'Tilt the card slightly if glare is present',
      'Keep the camera very steady',
    ];
    let tipIdx = 0;
    const tipInterval = setInterval(() => {
      if (!activeRef.current) { clearInterval(tipInterval); return; }
      tipIdx = (tipIdx + 1) % tips.length;
      setHint(tips[tipIdx]);
    }, 3000);

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Camera access is not available. The page must be served over HTTPS.');
          return;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });

        if (!activeRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        const video = videoRef.current;
        if (!video) return;

        video.srcObject = stream;
        await new Promise<void>(res => { video.onloadedmetadata = () => res(); });
        await video.play();

        // Match canvas to actual video dimensions
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = video.videoWidth || 1280;
          canvas.height = video.videoHeight || 720;
        }

        setScanning(true);

        // ── Try native BarcodeDetector first (Chrome/Edge — best PDF417 perf)
        if ('BarcodeDetector' in window) {
          try {
            const BD = (window as unknown as Record<string, unknown>).BarcodeDetector as {
              getSupportedFormats: () => Promise<string[]>;
              new(opts: { formats: string[] }): { detect: (src: HTMLCanvasElement) => Promise<Array<{ rawValue: string }>> };
            };
            const supported = await BD.getSupportedFormats();
            if (supported.includes('pdf417')) {
              const detector = new BD({ formats: ['pdf417'] });
              const tick = async () => {
                if (!activeRef.current || !canvas || !video) return;
                if (video.readyState === video.HAVE_ENOUGH_DATA) {
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    try {
                      const results = await detector.detect(canvas);
                      if (results.length > 0) {
                        handleResult(results[0].rawValue);
                        return;
                      }
                    } catch { /* try next frame */ }
                  }
                }
                rafRef.current = requestAnimationFrame(tick);
              };
              rafRef.current = requestAnimationFrame(tick);
              return;
            }
          } catch { /* native unavailable, fall through */ }
        }

        // ── Fallback: zxing PDF417 against canvas frames
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const { DecodeHintType, BarcodeFormat, NotFoundException } = await import('@zxing/library');
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.PDF_417]);
        hints.set(DecodeHintType.TRY_HARDER, true);
        const reader = new BrowserMultiFormatReader(hints);

        const tick = () => {
          if (!activeRef.current || !canvas || !video) return;
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              try {
                const result = (reader as unknown as { decodeFromCanvas: (c: HTMLCanvasElement) => { getText: () => string } | null }).decodeFromCanvas(canvas);
                if (result) {
                  handleResult(result.getText());
                  return;
                }
              } catch (e) {
                if (!(e instanceof NotFoundException)) { /* ignore */ }
              }
            }
          }
          rafRef.current = setTimeout(tick, 200);
        };
        tick();
      } catch (err) {
        if (!activeRef.current) return;
        const e = err as Error & { name?: string };
        setError(
          e.name === 'NotAllowedError' ? 'Camera permission denied. Allow camera access in browser settings.' :
          e.name === 'NotFoundError' ? 'No camera found on this device.' :
          e.name === 'NotReadableError' ? 'Camera in use by another app. Close it and try again.' :
          `Camera error: ${e.message}`,
        );
        setScanning(false);
      }
    };

    start();
    return () => {
      clearInterval(tipInterval);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  return (
    <Modal opened={opened} onClose={() => { stop(); onClose(); }} title={
      <Group gap="xs"><IconBarcode size={18} /><Text fw={600}>Scan Driver's License</Text></Group>
    } size="lg" centered>
      <Stack align="center" gap="md">
        {error ? (
          <Paper p="lg" withBorder radius="sm" style={{ textAlign: 'center' }}>
            <ThemeIcon size={48} radius="xl" variant="light" color="red" mx="auto" mb="md">
              <IconX size={24} />
            </ThemeIcon>
            <Text size="sm" c="red">{error}</Text>
          </Paper>
        ) : (
          <div style={{
            position: 'relative', width: '100%', maxWidth: 500, aspectRatio: '4/3',
            background: '#000', borderRadius: 8, overflow: 'hidden',
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {scanning && (
              <div style={{
                position: 'absolute', top: '20%', left: '10%', right: '10%', bottom: '20%',
                border: '2px solid rgba(75,63,255,0.7)', borderRadius: 8,
              }}>
                <div style={{
                  position: 'absolute', left: 0, right: 0, height: 2,
                  background: 'linear-gradient(90deg, transparent, var(--color-primary-700), transparent)',
                  animation: 'dl-scanline 2s ease-in-out infinite',
                  top: '50%',
                }} />
              </div>
            )}
            {!scanning && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Loader color="white" size="lg" />
              </div>
            )}
          </div>
        )}
        <Text size="sm" c="dimmed" ta="center">{hint}</Text>
        <Button variant="subtle" onClick={() => { stop(); onClose(); }} leftSection={<IconX size={14} />}>
          Cancel
        </Button>
      </Stack>
      <style>{`
        @keyframes dl-scanline {
          0%, 100% { top: 5%; }
          50% { top: 90%; }
        }
      `}</style>
    </Modal>
  );
}
